/**
 * ContractJobModal — create / edit a contract job (Contract-division project, dispatch + timesheets only).
 * Address is geocoded at save (same as a WO site) so GPS on-site works; geocode failure warns, never blocks.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { createContractJob, updateContractJob } from "@/lib/api";
import { JobTag, kindMeta, JOB_KINDS } from "@/components/dispatch/JobTag";

const BLURB = {
  contract: "Tracked in the Contract division's software. Dispatch and timesheets only — no work order, Foundation, invoicing or budgets.",
  overhead: "Non-billable service time (shop, vehicle, training…). Dispatch and timesheets only — address optional. Techs must enter notes on the timesheet.",
  warranty: "Return visit on a closed job or warranty work. Dispatch and timesheets only — no work order, Foundation or invoicing. Techs must enter notes on the timesheet.",
};
const NAME_HINT = { contract: "e.g. Lincoln HS Gym — partitions", overhead: "e.g. Shop Time", warranty: "e.g. Lincoln HS — bleacher warranty" };

const EMPTY = { name: "", address: "", address_line_2: "", city: "", state: "", zip_code: "", contact_name: "", contact_phone: "", contact_email: "", install_details: "", dot_inspection_required: false, notes: "" };

export default function ContractJobModal({ open, onOpenChange, job, onSaved, kind: kindProp, onComplete, onReopen }) {
  const [pickedKind, setPickedKind] = useState(null);
  const kind = job?.job_kind || pickedKind || kindProp || "contract";
  const meta = kindMeta(kind);
  const label = meta.label.toLowerCase();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setForm(job ? { ...EMPTY, ...job } : EMPTY); setPickedKind(null); } }, [open, job]);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Job name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, form[k]])), kind };
      const res = job?.id ? await updateContractJob(job.id, payload) : await createContractJob(payload);
      const saved = res.data;
      if (saved.geocode_warning) toast.warning(saved.geocode_warning, { duration: 8000 });
      else toast.success(`${meta.label} ${job?.id ? "updated" : "saved"}`);
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not save ${label}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[92vh] overflow-y-auto" data-testid="contract-job-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <JobTag kind={kind} size="sm" testId="contract-job-modal-tag" />
            {job?.id ? `Edit ${label}` : `New ${label}`}
          </DialogTitle>
          <p className="text-xs text-slate-500">{BLURB[kind]}</p>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {!job?.id && (
            <div className="sm:col-span-2">
              <Label className="text-xs">Job Type</Label>
              <div className="mt-1 flex gap-2" data-testid="cj-kind-picker">
                {Object.entries(JOB_KINDS).map(([k, m]) => (
                  <button key={k} type="button" onClick={() => setPickedKind(k)} data-testid={`cj-kind-${k}`}
                    className={`flex-1 rounded-md border px-2 py-2 text-xs font-semibold transition-colors ${kind === k ? "bg-slate-100 dark:bg-slate-800" : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60"}`}
                    style={kind === k ? { borderColor: m.color, boxShadow: `inset 0 0 0 1px ${m.color}` } : undefined}>
                    <span className="inline-block h-2.5 w-2.5 rounded-full mr-1.5 align-middle" style={{ backgroundColor: m.color }} />
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label className="text-xs">Job name *</Label>
            <Input value={form.name} onChange={set("name")} placeholder={NAME_HINT[kind]} data-testid="cj-name" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Site address{kind === "overhead" ? " (optional)" : ""}</Label>
            <Input value={form.address} onChange={set("address")} placeholder="Street" data-testid="cj-address" />
          </div>
          <div className="sm:col-span-2">
            <Input value={form.address_line_2} onChange={set("address_line_2")} placeholder="Suite / building (optional)" data-testid="cj-address2" />
          </div>
          <div><Input value={form.city} onChange={set("city")} placeholder="City" data-testid="cj-city" /></div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.state} onChange={set("state")} placeholder="State" data-testid="cj-state" />
            <Input value={form.zip_code} onChange={set("zip_code")} placeholder="ZIP" data-testid="cj-zip" />
          </div>
          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div><Label className="text-xs">Site Contact</Label><Input value={form.contact_name} onChange={set("contact_name")} placeholder="Name" data-testid="cj-contact-name" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.contact_phone} onChange={set("contact_phone")} placeholder="555-0100" data-testid="cj-contact-phone" /></div>
            <div><Label className="text-xs">Email</Label><Input value={form.contact_email} onChange={set("contact_email")} placeholder="name@site.com" data-testid="cj-contact-email" /></div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">{kind === "contract" ? "Install details" : "Work details"}</Label>
            <Textarea value={form.install_details} onChange={set("install_details")} rows={3} placeholder="What the techs are doing on site, equipment, access, staging…" data-testid="cj-install-details" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Field notes (initial)</Label>
            <Textarea value={form.notes} onChange={set("notes")} rows={2} placeholder="Anything the crew should know" data-testid="cj-notes" />
          </div>
          <label className="sm:col-span-2 flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
            <input type="checkbox" checked={!!form.dot_inspection_required} onChange={set("dot_inspection_required")} className="h-4 w-4" data-testid="cj-dot" />
            DOT trailer inspection required
          </label>
        </div>
        <DialogFooter className="sm:justify-between gap-2">
          <div>
            {job?.id && !job.archived && onComplete && (
              <Button variant="outline" onClick={() => { if (window.confirm(`Mark "${job.name}" complete?\n\nIt leaves the dispatch list; stops already on the schedule stay put.`)) onComplete(job); }}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="cj-complete">Mark Complete</Button>
            )}
            {job?.id && job.archived && onReopen && (
              <Button variant="outline" onClick={() => onReopen(job)} data-testid="cj-reopen">Reopen Job</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cj-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="cj-save">{saving ? "Saving…" : job?.id ? "Save changes" : `Save ${label}`}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
